import {RefObject, useCallback, useEffect, useRef} from 'react';

const INITIAL_FILTERS_POSITION_ERROR_RATE = 5;
const FIXED_HEADER_OFFSET_SCROLL_UP = 135;
const FIXED_HEADER_OFFSET_SCROLL_DOWN = 85;
const BOTTOM_THRESHOLD = 40;

interface IProps {
    containerRef: RefObject<HTMLDivElement>;
    filterRef: RefObject<HTMLDivElement>;
}

// Ссылка из макетов на аналогичное поведение
// https://www.lamoda.ru/c/355/clothes-zhenskaya-odezhda/?sitelink=topmenuW&l=3
// Макет https://www.figma.com/design/jZyHyRuc521ikC2Tx3EaFB/Web.%D0%9A%D0%B0%D1%82%D0%B0%D0%BB%D0%BE%D0%B3?node-id=6257-25275&p=f&t=c1q4A9mz2YYOg6DA-0
// Задача https://jira.awg.ru/browse/SIMPLENFR-1185
export const useStickyDesktopFilters = ({containerRef, filterRef}: IProps) => {
    const lastScrollYPositionRef = useRef<number>(0);

    const handleScroll = useCallback(() => {
        const containerElement = containerRef.current;
        const filtersElement = filterRef.current;

        if (!containerElement || !filtersElement) {
            return;
        }

        const {bottom, height, top} = filtersElement.getBoundingClientRect();

        const currentWindowScrollY = window.scrollY;
        const deltaWindowScrollY = currentWindowScrollY - lastScrollYPositionRef.current;
        const isScrollUp = 0 > deltaWindowScrollY;

        const newFiltersTopValue = top - deltaWindowScrollY;

        // Если скролл вверх
        if (isScrollUp) {
            const {top: containerTop} = containerElement.getBoundingClientRect();

            const isFiltersInInitialTopPosition = INITIAL_FILTERS_POSITION_ERROR_RATE >= Math.abs(containerTop - top);

            // Если блок с фильтрами в начальной позиции,
            // то делаем его статичным - как обычный элемент на странице
            if (isFiltersInInitialTopPosition) {
                filtersElement.style.position = '';
                filtersElement.style.top = '';
            } else {
                // Если блок с фильтрами перекрывается хедером,
                // то скролим его
                if (FIXED_HEADER_OFFSET_SCROLL_UP > newFiltersTopValue) {
                    const isNearTopPosition =
                        INITIAL_FILTERS_POSITION_ERROR_RATE >=
                        Math.abs(FIXED_HEADER_OFFSET_SCROLL_UP - newFiltersTopValue);
                    const newValue = isNearTopPosition ? FIXED_HEADER_OFFSET_SCROLL_UP : newFiltersTopValue;

                    filtersElement.style.position = 'sticky';
                    filtersElement.style.top = `${newValue}px`;
                }
            }

            // Если скролл вниз
        } else {
            const windowHeight = window.innerHeight;
            const isFiltersHeightMoreThanWindowHeight = height > windowHeight - FIXED_HEADER_OFFSET_SCROLL_DOWN;

            // Если блок с фильтрами больше высоты видимой области экрана
            if (isFiltersHeightMoreThanWindowHeight) {
                const maxTop = windowHeight - height - BOTTOM_THRESHOLD;
                const newTop = Math.max(newFiltersTopValue, maxTop);

                // Если доскролили до конца блока с фильтрами,
                // то делаем его стики
                if (bottom < windowHeight - BOTTOM_THRESHOLD) {
                    filtersElement.style.position = 'sticky';
                    filtersElement.style.top = `${newTop}px`;
                } else {
                    // Если еще не доскролили до конца блока с фильтрами,
                    // и он sticky, то скролим его
                    if ('sticky' === filtersElement.style.position) {
                        filtersElement.style.position = 'sticky';
                        filtersElement.style.top = `${newTop}px`;
                    }
                }
                // Если блок с фильтрами меньше высоты экрана
            } else {
                // Привязываем блок к верху экрана за вычетом фиксированного хедера
                filtersElement.style.position = 'sticky';
                filtersElement.style.top = `${FIXED_HEADER_OFFSET_SCROLL_DOWN}px`;
            }
        }

        lastScrollYPositionRef.current = currentWindowScrollY;
    }, [containerRef, filterRef]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);
};
